/**
 * Custom hooks for dbt data fetching and management
 * Uses react-query for data fetching, caching, and state management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dbtService from '../services/dbtService';
import { dbtConfig } from '../config/dbt';

// ============================================================================
// CONNECTION HOOKS
// ============================================================================

/**
 * Fetch all dbt connections
 */
export function useDbtConnections() {
  return useQuery({
    queryKey: ['dbt', 'connections'],
    queryFn: () => dbtService.getConnections(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2
  });
}

/**
 * Fetch a specific dbt connection
 */
export function useDbtConnection(connectionId) {
  return useQuery({
    queryKey: ['dbt', 'connection', connectionId],
    queryFn: () => dbtService.getConnection(connectionId),
    enabled: !!connectionId,
    staleTime: 5 * 60 * 1000
  });
}

/**
 * Create a new dbt connection
 */
export function useCreateDbtConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionData) => dbtService.createConnection(connectionData),
    onSuccess: () => {
      // Invalidate connections list to refetch
      queryClient.invalidateQueries({ queryKey: ['dbt', 'connections'] });
    }
  });
}

/**
 * Update a dbt connection
 */
export function useUpdateDbtConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ connectionId, connectionData }) =>
      dbtService.updateConnection(connectionId, connectionData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dbt', 'connections'] });
      queryClient.invalidateQueries({ queryKey: ['dbt', 'connection', variables.connectionId] });
    }
  });
}

/**
 * Delete a dbt connection
 */
export function useDeleteDbtConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId) => dbtService.deleteConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dbt', 'connections'] });
    }
  });
}

/**
 * Validate a dbt connection
 */
export function useValidateDbtConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId) => dbtService.validateConnection(connectionId),
    onSuccess: (_, connectionId) => {
      queryClient.invalidateQueries({ queryKey: ['dbt', 'connection', connectionId] });
      queryClient.invalidateQueries({ queryKey: ['dbt', 'connections'] });
    }
  });
}

/**
 * Process dbt Core artifacts
 */
export function useProcessArtifacts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ connectionId, artifactsPath }) =>
      dbtService.processArtifacts(connectionId, artifactsPath),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dbt', 'connection', variables.connectionId] });
    }
  });
}

// ============================================================================
// PROJECT DATA HOOKS
// ============================================================================

/**
 * Fetch project overview analytics
 */
export function useDbtProjectOverview(projectId) {
  return useQuery({
    queryKey: ['dbt', 'project', projectId, 'overview'],
    queryFn: () => dbtService.getProjectOverview(projectId),
    enabled: !!projectId,
    refetchInterval: dbtConfig.ui.chartRefreshInterval, // Auto-refresh every minute
    staleTime: 30 * 1000 // 30 seconds
  });
}

/**
 * Fetch all models for a project
 */
export function useDbtModels(projectId) {
  return useQuery({
    queryKey: ['dbt', 'project', projectId, 'models'],
    queryFn: () => dbtService.getProjectModels(projectId),
    enabled: !!projectId,
    staleTime: 10 * 60 * 1000 // 10 minutes
  });
}

/**
 * Fetch all tests for a project
 */
export function useDbtTests(projectId) {
  return useQuery({
    queryKey: ['dbt', 'project', projectId, 'tests'],
    queryFn: () => dbtService.getProjectTests(projectId),
    enabled: !!projectId,
    staleTime: 10 * 60 * 1000
  });
}

/**
 * Fetch latest run results for a project
 */
export function useDbtLatestRun(projectId) {
  return useQuery({
    queryKey: ['dbt', 'project', projectId, 'latest-run'],
    queryFn: () => dbtService.getLatestRun(projectId),
    enabled: !!projectId,
    refetchInterval: dbtConfig.polling.interval, // Poll every 30 seconds
    staleTime: 15 * 1000 // 15 seconds
  });
}

/**
 * Fetch failed tests for a project
 */
export function useFailedTests(projectId, runId = null) {
  return useQuery({
    queryKey: ['dbt', 'project', projectId, 'failed-tests', runId],
    queryFn: () => dbtService.getFailedTests(projectId, runId),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000
  });
}

// ============================================================================
// LINEAGE AND ANALYTICS HOOKS
// ============================================================================

/**
 * Fetch lineage for a specific model
 */
export function useModelLineage(projectId, modelUniqueId) {
  return useQuery({
    queryKey: ['dbt', 'project', projectId, 'model', modelUniqueId, 'lineage'],
    queryFn: () => dbtService.getModelLineage(projectId, modelUniqueId),
    enabled: !!projectId && !!modelUniqueId,
    staleTime: 10 * 60 * 1000
  });
}

/**
 * Fetch execution history for a specific test
 */
export function useTestHistory(projectId, testUniqueId, limit = 10) {
  return useQuery({
    queryKey: ['dbt', 'project', projectId, 'test', testUniqueId, 'history', limit],
    queryFn: () => dbtService.getTestHistory(projectId, testUniqueId, limit),
    enabled: !!projectId && !!testUniqueId,
    staleTime: 5 * 60 * 1000
  });
}

// ============================================================================
// HEALTH CHECK HOOK
// ============================================================================

/**
 * Check dbt integration health
 */
export function useDbtHealth() {
  return useQuery({
    queryKey: ['dbt', 'health'],
    queryFn: () => dbtService.healthCheck(),
    staleTime: 60 * 1000, // 1 minute
    retry: 1
  });
}
